import { Loader, Notice, PageHeader } from '@deque/cauldron-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams
} from 'react-router-dom';
import {
  type AdminAnnouncement,
  type AnnouncementInput,
  createAdminAnnouncement,
  getAdminAnnouncementById,
  updateAdminAnnouncement
} from '~/common/api-client';
import useAPI from '~/common/hooks/useAPI';
import { isNotFoundError } from '~/common/utils/errors';
import PageTitle from '~/modules/common/components/PageTitle';
import { useAuthContext } from '~/modules/common/contexts/auth';
import AnnouncementForm from '../../components/announcements/AnnouncementForm';
import styles from '../../components/announcements/Announcements.css';
import AnnouncementsList from '../../components/announcements/AnnouncementsList';

const ADMIN_GROUP = window.appConfig.ANNOUNCEMENTS_ADMIN_GROUP;

const PATHS = {
  LIST: '/internal/announcements/admin',
  NEW: '/internal/announcements/admin/new',
  EDIT: '/internal/announcements/admin/:id/edit'
} as const;

const editPath = (id: string): string =>
  `/internal/announcements/admin/${id}/edit`;

const NewAnnouncement = ({ token }: { token: string }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<{ duplicate?: AdminAnnouncement }>();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const onSubmit = async (input: AnnouncementInput) => {
    setSubmitting(true);
    setError(null);
    try {
      await createAdminAnnouncement({ token, body: input });
      history.push(PATHS.LIST);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('Failed to create the announcement. Please try again.')
      );
      setSubmitting(false);
    }
  };

  return (
    <AnnouncementForm
      mode="create"
      initial={location.state?.duplicate}
      submitting={submitting}
      error={error}
      onSubmit={onSubmit}
      onCancel={() => history.push(PATHS.LIST)}
    />
  );
};

const EditAnnouncement = ({ token }: { token: string }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const loader = React.useCallback(
    () => getAdminAnnouncementById(token, id),
    [token, id]
  );
  const { data, loading, error: loadError } = useAPI(loader);

  const onSubmit = async (input: AnnouncementInput) => {
    setSubmitting(true);
    setError(null);
    try {
      await updateAdminAnnouncement({ token, id, body: input });
      history.push(PATHS.LIST);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('Failed to update the announcement. Please try again.')
      );
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader label={t('Loading announcement')} />;
  }
  if (loadError || !data) {
    // A 404's raw message is the "not_found" error code; show a human message.
    const message =
      !loadError || isNotFoundError(loadError)
        ? t('Announcement not found')
        : loadError.message;
    return (
      <Notice type="caution" title={t('Could not load announcement')}>
        {message}
      </Notice>
    );
  }

  return (
    <AnnouncementForm
      mode="edit"
      initial={data}
      submitting={submitting}
      error={error}
      onSubmit={onSubmit}
      onCancel={() => history.push(PATHS.LIST)}
    />
  );
};

const CustomerAnnouncements: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const history = useHistory();

  const isAdmin =
    !!ADMIN_GROUP && (user?.groups.includes(ADMIN_GROUP) ?? false);

  if (user === undefined || !isAdmin) {
    return (
      <div className={styles.page}>
        <PageTitle title={t('Customer announcements')} />
        <PageHeader heading={<h1>{t('Customer announcements')}</h1>} />
        <h2>{t('You do not have permission to see this page.')}</h2>
      </div>
    );
  }

  const token = user.token;

  return (
    <div className={styles.page}>
      <PageTitle title={t('Customer announcements')} />
      <Switch>
        <Route exact path={PATHS.LIST}>
          <AnnouncementsList
            token={token}
            onNew={() => history.push(PATHS.NEW)}
            onEdit={announcement => history.push(editPath(announcement.id))}
            onDuplicate={announcement =>
              history.push({
                pathname: PATHS.NEW,
                state: { duplicate: announcement }
              })
            }
          />
        </Route>
        <Route exact path={PATHS.NEW}>
          <NewAnnouncement token={token} />
        </Route>
        <Route exact path={PATHS.EDIT}>
          <EditAnnouncement token={token} />
        </Route>
      </Switch>
    </div>
  );
};

export default CustomerAnnouncements;
